// Mock data for the "Nhật ký giao dịch" (trading journal) day-detail screen.
export type StepKey = 0 | 1 | 2 | 3 | 4;

// A selectable chip/option with a stable id and a display label.
export interface Option {
  id: string;
  label: string;
}

export type MoodKind = 'good' | 'bad';
export type MoodTone = 'pos' | 'neg' | 'danger';
export type TradeDir = 'buy' | 'sell' | 'out';

// A price level the trader marks as important (support / resistance zone).
export interface KeyLevel {
  id: number;
  zone: string;
  type: string;
}

// Step metadata
export interface JournalStep {
  label: string;
  sub: string;
  timing: string;
  desc: string;
}

export const journalSteps: JournalStep[] = [
  { label: 'Đầu ngày — Thiết lập kế hoạch bảo vệ vốn', sub: 'Trước 11h sáng', timing: 'Thực hiện trước 9h00', desc: 'Đặt giới hạn rủi ro và cam kết kỷ luật trước khi thị trường mở.' },
  { label: 'Trước lệnh', sub: 'Duyệt khi vào', timing: 'Duyệt khi vào lệnh', desc: 'Rà soát điều kiện & thông số trước khi đặt lệnh.' },
  { label: 'Trong lệnh', sub: 'Giám sát vị thế', timing: 'Trong khi giữ lệnh', desc: 'Theo dõi và quản lý vị thế theo đúng kế hoạch.' },
  { label: 'Sau lệnh', sub: 'Ghi lại bài học', timing: 'Sau khi đóng lệnh', desc: 'Ghi nhận kết quả và rút ra bài học sau khi đóng lệnh.' },
  { label: 'Cuối ngày', sub: 'Tổng kết chấm điểm', timing: 'Cuối phiên giao dịch', desc: 'Tổng kết ngày giao dịch và tự chấm điểm kỷ luật.' },
];

export interface TradingAccount {
  id: string;
  name: string;
  broker: string;
  pair: string;
  stage: string;
  plat?: 'mt4' | 'mt5' | 'ct';
  balance?: number;
  status?: 'live' | 'risk' | 'off';
  currency?: string;
  index: number;
}

export const tradingAccounts: TradingAccount[] = [];

// Per-step checklist items, keyed by step.
// Health & psychology
export const healthConditionItems: Option[] = [
  { id: 'h1', label: 'Ngủ đủ giấc (>7h)' },
  { id: 'h2', label: 'Tinh thần minh mẫn, không buồn ngủ' },
  { id: 'h3', label: 'Không bận việc gia đình / công sở' },
  { id: 'h4', label: 'Không gian làm việc yên tĩnh' },
];

export const healthConditionTags = ['Rất kém', 'Kém', 'Tạm ổn', 'Tốt', 'Rất tốt'];

export interface MoodOption extends Option {
  kind: MoodKind;
}
export const moodOptions: MoodOption[] = [
  { id: 'calm', label: 'Bình tĩnh', kind: 'good' }, { id: 'confident', label: 'Tự tin', kind: 'good' }, { id: 'focused', label: 'Tập trung', kind: 'good' }, { id: 'happy', label: 'Vui', kind: 'good' },
  { id: 'anxious', label: 'Lo lắng', kind: 'bad' }, { id: 'greedy', label: 'Tham lam', kind: 'bad' }, { id: 'fearful', label: 'Sợ hãi', kind: 'bad' }, { id: 'tired', label: 'Mệt mỏi', kind: 'bad' },
  { id: 'fomo', label: 'FOMO', kind: 'bad' }, { id: 'sad', label: 'Buồn', kind: 'bad' }, { id: 'bored', label: 'Chán', kind: 'bad' },
];

// Market context
export const sessionOptions: Option[] = [
  { id: 'Á (Asian)', label: 'Phiên Á' },
  { id: 'Âu (London)', label: 'Phiên Âu' },
  { id: 'Mỹ (New York)', label: 'Phiên Mỹ' },
  { id: 'Giao thoa Âu–Mỹ', label: 'Giao thoa Âu–Mỹ' },
];

// Key Level types (for the "Giá đang tại Key Level quan trọng" inputs)
export const keyLevelTypeOptions = ['Hỗ trợ', 'Kháng cự'];

export const structureOptions = ['Tăng (Bullish)', 'Giảm (Bearish)', 'Sideway'];
export const h1m15ConsensusOptions = ['Đồng thuận xu hướng lớn', 'Ngược xu hướng chính', 'Nhiễu loạn'];

// Economic news
export type NewsLevel = 'high' | 'medium' | 'low';
export interface NewsItem {
  name: string;
  time: string;
  level: NewsLevel;
  action: string;
}
export const economicNews: NewsItem[] = [
  { name: 'Bảng lương phi nông nghiệp (NFP)', time: '19:30', level: 'high', action: 'Đứng ngoài 30 phút trước & sau tin...' },
  { name: 'Chỉ số CPI (Mỹ)', time: '19:30', level: 'high', action: 'Không vào lệnh mới...' },
  { name: 'Tồn kho dầu thô (EIA)', time: '21:30', level: 'medium', action: 'Giảm khối lượng...' },
  { name: 'Số đơn xin trợ cấp thất nghiệp', time: '19:30', level: 'low', action: 'Giao dịch bình thường...' },
];

export const rrRatioOptions = ['1:1.5', '1:2', '1:3', '1:4'];

// Pre-trade review (step 1)
export interface PillarItem {
  label: string;
  ok: boolean;
}
export interface Pillar {
  title: string;
  subtitle: string;
  max: number;
  score: number;
  iconPath: string;
  items: PillarItem[];
}
export const pretradePillars: Pillar[] = [
  { title: 'Bối cảnh', subtitle: 'Cấu trúc & xu hướng', max: 20, score: 18, iconPath: 'M3 17l6-6 4 4 8-8M14 7h7v7', items: [{ label: 'D1/H4 đồng pha xu hướng', ok: true }, { label: 'H1/M15 đồng thuận', ok: true }, { label: 'Vùng hỗ trợ/kháng cự rõ', ok: true }] },
  { title: 'Môi trường', subtitle: 'Tin tức & thanh khoản', max: 20, score: 11, iconPath: 'M3 12h4l3 8 4-16 3 8h4', items: [{ label: 'Không có tin Cao trong 1h', ok: false }, { label: 'Phiên có thanh khoản tốt', ok: true }, { label: 'Spread/biến động bình thường', ok: true }] },
  { title: 'Setup', subtitle: 'Kế hoạch BUY / SELL', max: 20, score: 16, iconPath: 'M4 7h16M4 12h16M4 17h10', items: [{ label: 'Đúng setup A+ trong kế hoạch', ok: true }, { label: 'R:R ≥ 1:2', ok: true }, { label: 'Trigger xác nhận rõ ràng', ok: false }] },
  { title: 'Risk', subtitle: 'Quản trị vốn', max: 20, score: 20, iconPath: 'M12 3l8 4.5v9L12 21l-8-4.5v-9z', items: [{ label: 'Rủi ro ≤ 1% mỗi lệnh', ok: true }, { label: 'Trong giới hạn lỗ ngày', ok: true }, { label: 'Còn slot lệnh cho phép', ok: true }] },
  { title: 'Tâm lý', subtitle: 'Thể trạng & cảm xúc', max: 20, score: 13, iconPath: 'M12 21s-7-4.5-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6c-2.5 4.5-9.5 9-9.5 9z', items: [{ label: 'Thể trạng đạt yêu cầu', ok: true }, { label: 'Không cảm xúc tiêu cực', ok: false }, { label: 'Cam kết kỷ luật rõ', ok: true }] },
];

// Section 2 — Chất lượng Setup
export const setupCriteria: Option[] = [
  { id: 's1', label: 'Đúng setup đã được phép giao dịch — đúng hình mẫu đã backtest' },
  { id: 's2', label: 'Có tín hiệu xác nhận rõ ràng' },
  { id: 's3', label: 'RR tối thiểu đạt 1:2' },
];

export type PreStateKind = 'good' | 'bad';
export interface PreTradeState extends Option {
  kind: PreStateKind;
}
export const preTradeStates: PreTradeState[] = [
  { id: 'normal', label: 'Bình thường', kind: 'good' },
  { id: 'afterloss', label: 'Vừa thua lệnh trước', kind: 'bad' },
  { id: 'afterbigwin', label: 'Vừa thắng lớn', kind: 'bad' },
  { id: 'missed', label: 'Lỡ điểm vào trước đó', kind: 'bad' },
];
export const preStateWarnings: Record<string, string> = {
  afterloss: 'Vừa thua — dễ vào lệnh trả thù. Cân nhắc nghỉ hoặc giảm khối lượng.',
  afterbigwin: 'Vừa thắng lớn — dễ tự tin thái quá. Giữ kỷ luật, đúng kế hoạch.',
  missed: 'Lỡ điểm vào — dễ FOMO đuổi giá. Chỉ vào nếu vẫn đúng setup.',
};

// Section 3 — Quản trị rủi ro checklist
export interface RiskCriterion extends Option {
  hint: string;
}
export const riskCriteria: RiskCriterion[] = [
  { id: 'r1', label: 'Đã tính Lot Size theo SL', hint: 'Sử dụng máy tính Risk' },
  { id: 'r2', label: 'Risk nằm trong kế hoạch', hint: 'Không vượt mức % đã cam kết' },
  { id: 'r3', label: 'Đã đặt Stop Loss (SL)', hint: 'Nếu lệnh thua, tài khoản vẫn an toàn' },
  { id: 'r4', label: 'Tỷ lệ Risk : Reward đạt yêu cầu', hint: 'Tối thiểu 1:2' },
  { id: 'r5', label: 'Tổng rủi ro lệnh mở không vượt giới hạn', hint: 'Ví dụ < 5% tài khoản' },
  { id: 'r6', label: 'Không vào nhiều lệnh cùng chiều cặp tương quan', hint: 'EURUSD, GBPUSD…' },
  { id: 'r7', label: 'Đã xác định Take Profit (TP)', hint: 'Trước khi vào lệnh' },
  { id: 'r8', label: 'Không giao dịch khi Spread bất thường', hint: 'Hoặc thanh khoản thấp' },
  { id: 'r9', label: 'Đã kiểm tra lịch tin tức quan trọng', hint: 'NFP, CPI, FOMC…' },
  { id: 'r10', label: 'Không tăng khối lượng để gỡ lỗ', hint: 'Không Martingale' },
  { id: 'r11', label: 'Không vào lệnh nếu SL quá xa', hint: 'Làm tỷ lệ LN/RR không đạt' },
  { id: 'r12', label: 'Kiểm tra Margin Level còn an toàn', hint: 'Sau khi mở lệnh' },
  { id: 'r13', label: 'Không vượt số lệnh tối đa', hint: 'Theo kế hoạch giao dịch' },
];

// Section 4 — Tâm lý: mood groups
export interface MoodChipOption extends Option {
  tone: MoodTone;
}
export const moodQuick: MoodChipOption[] = [
  { id: 'p_calm', label: '🧘 Bình tĩnh', tone: 'pos' }, { id: 'p_focus', label: '🎯 Tập trung', tone: 'pos' }, { id: 'p_conf', label: '😌 Tự tin', tone: 'pos' },
  { id: 'n_impat', label: '😤 Nóng ruột', tone: 'neg' }, { id: 'n_anx', label: '😰 Lo lắng', tone: 'neg' },
  { id: 'd_fomo', label: '🚀 FOMO', tone: 'danger' }, { id: 'd_revenge', label: '🤕 Muốn gỡ lỗ', tone: 'danger' }, { id: 'd_overconf', label: '😎 Tự tin quá mức', tone: 'danger' },
];
export const moodPositive: Option[] = [
  { id: 'p_calm', label: '😀 Bình tĩnh' }, { id: 'p_conf', label: '😌 Tự tin' }, { id: 'p_focus', label: '🎯 Tập trung' }, { id: 'p_patient', label: '🧘 Kiên nhẫn' }, { id: 'p_disc', label: '💪 Kỷ luật' }, { id: 'p_relax', label: '😎 Thoải mái' }, { id: 'p_obj', label: '🔍 Khách quan' },
];
export const moodNegative: Option[] = [
  { id: 'n_anx', label: '😰 Lo lắng' }, { id: 'n_fear', label: '😨 Sợ hãi' }, { id: 'n_impat', label: '😤 Nóng ruột' }, { id: 'n_angry', label: '😡 Bực tức' }, { id: 'n_stress', label: '😫 Căng thẳng' }, { id: 'n_confuse', label: '😵 Hoang mang' }, { id: 'n_unconf', label: '😞 Thiếu tự tin' }, { id: 'n_nomot', label: '😔 Mất động lực' },
];
export const moodDanger: Option[] = [
  { id: 'd_revenge', label: '🤕 Muốn gỡ lỗ' }, { id: 'd_greed', label: '🤑 Tham lam' }, { id: 'd_overconf', label: '😎 Tự tin quá mức' }, { id: 'd_gamble', label: '🎲 Muốn đánh cược' }, { id: 'd_fomo', label: '🚀 FOMO' }, { id: 'd_euphoria', label: '⚡ Hưng phấn sau chuỗi thắng' }, { id: 'd_tilt', label: '💥 Cay cú sau chuỗi thua' }, { id: 'd_tired', label: '😴 Mệt mỏi' }, { id: 'd_sleepy', label: '🥱 Buồn ngủ' }, { id: 'd_distract', label: '📱 Mất tập trung' },
];

// Sections currently hold this state locally; lift it here when wiring the API.
// Step 0 §1 — Mục tiêu & Quản trị rủi ro
export interface RiskPlanDraft {
  riskPerTrade: string;
  dailyLoss: string;
  maxTrades: string;
  tradeReady: 'ok' | 'no' | null;
  stopAtTarget: 'sure' | 'no' | null;
}

// Step 0 §2 — Thể trạng & Sức khỏe tâm lý
export interface HealthDraft {
  conditions: Record<string, number>;
  moods: string[];
}

// Step 0 §3 / Step 1 §1 — Bối cảnh thị trường
export interface MarketContextDraft {
  d1?: string;
  h4?: string;
  h1m15?: string;
  dir?: TradeDir | null;
  reason?: string;
  keyLevels: KeyLevel[];
  liqType: string | null;
  scenarioNote: string;
  clean: 'clean' | 'noise' | null;
  sessions: string[];
}

export interface NewsHandlingDraft {
  actions: Record<number, string>;
}

// Step 0 §5/§6 — Kế hoạch BUY / SELL Setup
export interface SetupPlanDraft {
  side: 'buy' | 'sell';
  price: string;
  band: string;
  rr: string | null;
  sl: string;
  tp: string;
  trigger: string;
  cancel: string;
}

// Step 0 §7 — Dự báo kịch bản phiên
export interface ScenarioDraft {
  id: number;
  cond: string;
  act: string;
  prob: number;
  dir?: 'buy' | 'sell' | 'wait';
  img?: string;
}

// Step 1 §2 — Chất lượng Setup
export interface SetupQualityDraft {
  checks: Record<string, boolean>;
  preState: string | null;
}

// Step 1 §3 — Quản trị rủi ro checklist
export interface RiskChecklistDraft {
  checks: Record<string, boolean>;
}

// Step 1 §4 — Tâm lý giao dịch
export interface PsychologyDraft {
  moods: string[];
  wcSL: string;
  wcDD: string;
  accept: 'yes' | 'maybe' | 'no' | null;
}

// Whole-day journal payload. `date` + `accountId` identify the record;
// the rest are the per-step drafts, optional until that step is filled.
export interface DayJournalDraft {
  date: string;
  accountId: string;
  risk?: RiskPlanDraft;
  health?: HealthDraft;
  market?: MarketContextDraft;
  buySetup?: SetupPlanDraft;
  sellSetup?: SetupPlanDraft;
  scenarios?: ScenarioDraft[];
  setupQuality?: SetupQualityDraft;
  riskChecklist?: RiskChecklistDraft;
  psychology?: PsychologyDraft;
  inTrade?: InTradeDraft;
  postTrade?: PostTradeDraft;
  endOfDay?: EndOfDayDraft;
}

// Open / closed trades the journal works against. An API would return these
export interface Trade {
  id: string;
  side: 'buy' | 'sell';
  pair: string;
  lot: number;
  entry: number;
  sl: number;
  tp: number;
  status: 'open' | 'closed';
  // Filled once the trade is closed (step 3 onward):
  outcome?: 'win' | 'loss' | 'be';
  rr?: string;
  pnl?: number;
}
export const journalTrades: Trade[] = [
  { id: '#001', side: 'buy', pair: 'XAUUSD', lot: 0.1, entry: 2320.5, sl: 2315.0, tp: 2341.0, status: 'closed', outcome: 'win', rr: '1:2.1', pnl: 210 },
  { id: '#002', side: 'sell', pair: 'XAUUSD', lot: 0.05, entry: 2358.0, sl: 2363.0, tp: 2348.0, status: 'closed', outcome: 'loss', rr: '1:2.0', pnl: -125 },
];

// Step 2 — Market phase (per trade)
export const marketPhaseConditions: Option[] = [
  { id: '21', label: 'D1 – H4 – H1 đã xác định hướng chính' },
  { id: '22', label: 'Giá đang tại Key Level quan trọng' },
  { id: '23', label: 'Thanh khoản gần nhất đã được xác định' },
];

export const marketPhaseTypes: Option[] = [
  { id: 'continuation', label: 'Continuation' },
  { id: 'reversal', label: 'Reversal' },
  { id: 'sweep', label: 'Sweep' },
  { id: 'breakout', label: 'Breakout' },
  { id: 'retest', label: 'Retest' },
];

// Step 2 — In-trade discipline checklist (per trade)
export const inTradeChecklist: Option[] = [
  { id: '17', label: 'Không dời Stop Loss' },
  { id: '18', label: 'Không chốt non vì sợ' },
  { id: '19', label: 'Tuân thủ kế hoạch quản lý vị thế' },
  { id: '20', label: 'Chấp nhận kết quả giao dịch' },
];

// Step 2 — "Tôi đang cảm thấy gì?" quick feelings
export const inTradeFeelings: Option[] = [
  { id: 'fear_loss', label: '😨 Sợ mất lợi nhuận' },
  { id: 'early_exit', label: '⚡ Muốn chốt non' },
  { id: 'move_sl', label: '🔄 Muốn dời Stop Loss' },
  { id: 'add_more', label: '➕ Muốn nhồi thêm lệnh' },
  { id: 'calm', label: '✅ Bình tĩnh, tin vào kế hoạch' },
];

// Step 3 — Post-trade close-out checklist
export const postTradeChecklist: Option[] = [
  { id: '10', label: 'Đã đóng lệnh theo kế hoạch' },
  { id: '11', label: 'Lưu ảnh chụp biểu đồ vào/ra' },
  { id: '12', label: 'Đánh giá cảm xúc trung thực' },
];

// Step 3 — Emotion after closing a trade
export const postTradeEmotions: Option[] = [
  { id: 'satisfied', label: 'Hài lòng' },
  { id: 'regret', label: 'Tiếc nuối' },
  { id: 'relieved', label: 'Nhẹ nhõm' },
  { id: 'frustrated', label: 'Bực bội' },
  { id: 'neutral', label: 'Bình thản' },
];

// Step 4 — End-of-day checklist
export const eodChecklist: Option[] = [
  { id: 'eod1', label: 'Đã ghi nhật ký tất cả các lệnh' },
  { id: 'eod2', label: 'Cập nhật KPI trong ngày' },
  { id: 'eod3', label: 'Review & sao lưu lại ngày' },
];

// Step 4 — Trade-quality yes/no questions
export const tradeQualityQuestions: Option[] = [
  { id: 'q1', label: 'Lệnh này có đúng setup không?' },
  { id: 'q2', label: 'Có giữ đúng risk không?' },
  { id: 'q3', label: 'Có FOMO hoặc revenge trade không?' },
  { id: 'q4', label: 'Có quản lý lệnh đúng kế hoạch không?' },
  //{ id: 'q5', label: 'Lệnh này có nên lặp lại trong tương lai không?' },
];

// Step 4 — Discipline self-rating criteria (each scored 0..max)
export const disciplineCriteria: Option[] = [
  { id: 'dc1', label: 'Kỷ luật tuân thủ' },
  { id: 'dc2', label: 'Bám sát kế hoạch' },
  { id: 'dc3', label: 'Quản trị rủi ro' },
  { id: 'dc4', label: 'Kiểm soát tâm lý' },
];
export const disciplineRatingMax = 5;

// Maps a 0..20 discipline score to a grade band.
export function disciplineGrade(score: number): 'A' | 'B' | 'C' | 'D' {
  if (score >= 18) return 'A';
  if (score >= 14) return 'B';
  if (score >= 10) return 'C';
  return 'D';
}

// Step 2 — Trong lệnh. Every field is per-trade.
export interface InTradeEntryDraft {
  checks: Record<string, boolean>;
  note: string;
  feelings: string[];
  adjustments: string;
}
export interface InTradeDraft {
  perTrade: Record<string, InTradeEntryDraft>;
}

// Step 3 — Sau lệnh.
export interface PostTradeEntryDraft {
  emotion: string | null;
  bestThing: string;
  biggestMistake: string;
  checks: Record<string, boolean>;
  lesson: string;
  mistakeToAvoid: string;
}
export interface PostTradeDraft {
  perTrade: Record<string, PostTradeEntryDraft>;
}

// Step 4 — Cuối ngày
export interface EndOfDayDraft {
  checks: Record<string, boolean>;
  daySummary: string;
  quality: Record<string, boolean | null>;
  ratings: Record<string, number>;
  bestThing: string;
  biggestMistake: string;
}
